import { ProgramCurrency } from '@models/membership/ProgramCurrency'
import { programCurrencySchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createProgramCurrency as createProgramCurrencyAPI,
  deleteProgramCurrency as deleteProgramCurrencyAPI,
  fetchProgramCurrencies,
} from '@services/membership/ProgramCurrencyService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<ProgramCurrency> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'programCurrency',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getProgramCurrenciesSuccess,
  setQuery: setProgramCurrencyQuery,
  reset: resetProgramCurrencyState,
  resetQueryAndIds: resetProgramCurrencyQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: ProgramCurrency }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectProgramCurrencies = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedProgramCurrency = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectProgramCurrencyById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectProgramCurrenciesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedProgramCurrency =
  (currency: ProgramCurrency | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<ProgramCurrency>(currency))
    )

export const getProgramCurrencies = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchProgramCurrencies(params),
    [programCurrencySchema],
    slice.actions
  )

export const createProgramCurrency = (currency: ProgramCurrency) =>
  SBAPI.CreateDispatched(
    createProgramCurrencyAPI(currency),
    programCurrencySchema,
    slice.actions
  )

export const deleteProgramCurrency = (currency: ProgramCurrency) =>
  SBAPI.DestroyDispatched<ProgramCurrency>(
    deleteProgramCurrencyAPI(currency),
    currency,
    slice.actions
  )
